import { PredefinedMilisecondsTypeEnum } from '../../enums';
import { StockExchangeCodeType } from '../../models/fundamental/types';
import { isEmpty } from '../../util/typeGuards';

import { BaseSubscriptionService } from './BaseSubscription.service';
import { chunkSymbols } from './helper';
import { SymbolSubscriptionRequestType } from './types';

interface PendingUnsubscription {
  symbols: string[];
  stockExchangeCode?: StockExchangeCodeType;
  timeoutId: ReturnType<typeof setTimeout>;
}

export abstract class BaseSymbolSubscriptionService<T extends SymbolSubscriptionRequestType>
  extends BaseSubscriptionService<T> {
  private pendingUnsubscriptions: PendingUnsubscription[] = [];

   
  protected constructor() {
    super();
  }

  private cancelPendingUnsubscriptions(
    symbols: string[],
    stockExchangeCode?: StockExchangeCodeType,
  ): void {
    this.pendingUnsubscriptions = this.pendingUnsubscriptions.filter(pending => {
      if (pending.stockExchangeCode === stockExchangeCode) {
        const hasOverlap = pending.symbols.some(symbol => symbols.includes(symbol));
        if (hasOverlap) {
          clearTimeout(pending.timeoutId);
          return false; // Remove from pending list
        }
      }
      return true; // Keep in pending list
    });
  }

  private scheduleDelayedUnsubscription(
    symbols: string[],
    stockExchangeCode?: StockExchangeCodeType,
  ): void {
    const timeoutId = setTimeout(() => {
      this.executeUnsubscription(symbols, stockExchangeCode);
      // Remove from pending list
      this.pendingUnsubscriptions = this.pendingUnsubscriptions.filter(p => p.timeoutId !== timeoutId);
    }, PredefinedMilisecondsTypeEnum.fiveSeconds);

    this.pendingUnsubscriptions.push({
      symbols,
      stockExchangeCode,
      timeoutId,
    });
  }

  private executeUnsubscription(
    symbols: string[],
    stockExchangeCode?: StockExchangeCodeType,
  ): void {
    const symbolChunks = chunkSymbols(symbols);
    symbolChunks.forEach(symbolChunk => {
      this.gatewayService.sendRequest({
        ...this.getDefaultRequestParams(),
        symbols: symbolChunk,
        stockExchangeCode,
        messageType: this.getUnsubscriptionRequestType(),
      });
    });
  }

  public subscribe(
    symbol: string,
    stockExchangeCode?: StockExchangeCodeType,
  ): void {
    const requestByBucketAndExchange = this.getRequestByBucketAndExchange(stockExchangeCode);

    if (!requestByBucketAndExchange) {
      const request = {
        symbols: [ symbol ],
        stockExchangeCode,
        ...this.getDefaultRequestParams(),
      } as T;
      this.sendRequestToGw(request);
      return;
    }

    if (requestByBucketAndExchange.symbols.includes(symbol)) { // This symbol already in the subscription request list!
      return;
    }

    requestByBucketAndExchange.symbols.push(symbol); // add symbol to related list
    this.gatewayService.sendRequest({
      symbols: [ symbol ],
      stockExchangeCode,
      ...this.getDefaultRequestParams(),
    });
  }

  public subscribeList(
    symbols: string[],
    stockExchangeCode?: StockExchangeCodeType,
  ): void {
    this.cancelPendingUnsubscriptions(symbols, stockExchangeCode);

    const requestByBucketAndExchange = this.getRequestByBucketAndExchange(stockExchangeCode);

    if (!requestByBucketAndExchange) {
      const symbolChunks = chunkSymbols(symbols);
      symbolChunks.forEach(symbolChunk => {
        const request = {
          symbols: symbolChunk,
          stockExchangeCode,
          ...this.getDefaultRequestParams(),
        } as T;
        this.sendRequestToGw(request);
      });
      return;
    }

    const notRequestedList = symbols.filter(item => !requestByBucketAndExchange?.symbols.includes(item)) || [];

    if (isEmpty(notRequestedList)) { // All symbols are already added to subscription request list!
      return;
    }

    const symbolChunks = chunkSymbols(notRequestedList);
    symbolChunks.forEach(symbolChunk => {
      requestByBucketAndExchange.symbols = [ ...requestByBucketAndExchange?.symbols || [], ...symbolChunk ]; // add symbols to related list
      this.gatewayService.sendRequest({
        symbols: symbolChunk,
        stockExchangeCode,
        ...this.getDefaultRequestParams(),
      });
    });
  }

  public unsubscribe(
    symbol: string,
    stockExchangeCode?: StockExchangeCodeType,
  ): void {
    const requestByBucketAndExchange = this.getRequestByBucketAndExchange(stockExchangeCode);

    if (!requestByBucketAndExchange) { // There is no request in the list by the given exchange!
      return;
    }

    if (!requestByBucketAndExchange.symbols.includes(symbol)) { // There is no request in the list by the given symbol!
      return;
    }

    requestByBucketAndExchange.symbols = requestByBucketAndExchange.symbols.filter(item => item !== symbol); // remove only given symbol from the list

    this.scheduleDelayedUnsubscription([ symbol ], stockExchangeCode);
  }

  public unsubscribeList(
    symbols: string[],
    stockExchangeCode?: StockExchangeCodeType,
  ): void {
    const requestByBucketAndExchange = this.getRequestByBucketAndExchange(stockExchangeCode) as T;

    if (!requestByBucketAndExchange) { // There is no request in the list by the given exchange!
      return;
    }

    const requestedList = symbols.filter(item => requestByBucketAndExchange?.symbols.includes(item)) || [];

    if (isEmpty(requestedList)) { // The symbols are not in the subscription request list!
      return;
    }

    requestByBucketAndExchange.symbols = requestByBucketAndExchange.symbols.filter(
      item => !requestedList.includes(item),
    ); // remove given symbols from the list

    this.scheduleDelayedUnsubscription(requestedList, stockExchangeCode);
  }

  private getRequestByBucketAndExchange(
    stockExchangeCode?: StockExchangeCodeType,
  ): T | undefined {
    return this.subscripitonRequests.find(
      item => stockExchangeCode && item.stockExchangeCode === stockExchangeCode,
    );
  }
}
